import '../ig-story.css'
import QueuePagination from '../queue-pagination'
import { getIgStorySaDisplayName } from '../sa-display-name'
import { AvailabilityBadge } from '../availability-presentation'
import { CountOptions } from '../report-filters'
import SalesIgStoryApi from 'api/admins/sales-ig-story-api'
import { TitlePage } from 'components/atoms'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupAddon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table
} from 'reactstrap'

const emptyMeta = { current_page: 1, per_page: 15, total_count: 0, total_pages: 1 }
const emptyViewer = { can_fill_bucket: false, can_swap_assignments: false }

const formatDateTime = (value) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('en-GB', { timeZone: 'Asia/Jakarta', hour12: false })
}

const setRowSelection = ({
  checked,
  idSetter,
  rowSetter,
  row
}) => {
  idSetter((current) => (
    checked
      ? (current.includes(row.id) ? current : [...current, row.id])
      : current.filter((value) => value !== row.id)
  ))

  rowSetter((current) => {
    const next = { ...current }
    if (checked) next[row.id] = row
    else delete next[row.id]
    return next
  })
}

const SalesIgStorySaAssignmentDetailPage = ({ history, match }) => {
  const administratorId = match.params.administratorId
  const [member, setMember] = useState(null)
  const [summary, setSummary] = useState({ assigned: 0, replaced: 0, completed: 0, capacity: 0 })
  const [fillPreview, setFillPreview] = useState({})
  const [viewer, setViewer] = useState(emptyViewer)
  const [assigned, setAssigned] = useState([])
  const [replaced, setReplaced] = useState([])
  const [assignedMeta, setAssignedMeta] = useState(emptyMeta)
  const [replacedMeta, setReplacedMeta] = useState(emptyMeta)
  const [assignedPage, setAssignedPage] = useState(1)
  const [replacedPage, setReplacedPage] = useState(1)
  const [assignedQInput, setAssignedQInput] = useState('')
  const [assignedQ, setAssignedQ] = useState('')
  const [replacedQInput, setReplacedQInput] = useState('')
  const [replacedQ, setReplacedQ] = useState('')
  const [assignedArrivalFrom, setAssignedArrivalFrom] = useState('')
  const [assignedArrivalTo, setAssignedArrivalTo] = useState('')
  const [assignedCountFilter, setAssignedCountFilter] = useState('')
  const [replacedAtFrom, setReplacedAtFrom] = useState('')
  const [replacedAtTo, setReplacedAtTo] = useState('')
  const [replacedCountFilter, setReplacedCountFilter] = useState('')
  const [selectedAssignedIds, setSelectedAssignedIds] = useState([])
  const [selectedReplacedIds, setSelectedReplacedIds] = useState([])
  const [selectedAssignedById, setSelectedAssignedById] = useState({})
  const [selectedReplacedById, setSelectedReplacedById] = useState({})
  const [loading, setLoading] = useState(false)
  const [swapping, setSwapping] = useState(false)
  const [filling, setFilling] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [fillConfirmOpen, setFillConfirmOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setErrorMessage('')
    try {
      const response = await SalesIgStoryApi.saAssignmentDetail({
        administratorId,
        params: {
          assigned_page: assignedPage,
          replaced_page: replacedPage,
          assigned_q: assignedQ || undefined,
          replaced_q: replacedQ || undefined,
          assigned_arrival_from: assignedArrivalFrom || undefined,
          assigned_arrival_to: assignedArrivalTo || undefined,
          assigned_count_filter: assignedCountFilter || undefined,
          replaced_at_from: replacedAtFrom || undefined,
          replaced_at_to: replacedAtTo || undefined,
          replaced_count_filter: replacedCountFilter || undefined
        }
      })
      const data = response.data || {}
      setMember(data.member || null)
      setSummary(data.summary || {})
      setFillPreview(data.fill_preview || {})
      setViewer(data.viewer || emptyViewer)
      setAssigned(data.assigned || [])
      setReplaced(data.replaced || [])
      setAssignedMeta(data.assigned_meta || emptyMeta)
      setReplacedMeta(data.replaced_meta || emptyMeta)
    }
    catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Gagal memuat SA Assignment Detail.')
    }
    finally {
      setLoading(false)
    }
  }, [
    administratorId,
    assignedPage,
    replacedPage,
    assignedQ,
    replacedQ,
    assignedArrivalFrom,
    assignedArrivalTo,
    assignedCountFilter,
    replacedAtFrom,
    replacedAtTo,
    replacedCountFilter
  ])

  useEffect(() => { load() }, [load])

  const queueCount = Number(summary.queue_size ?? summary.assigned ?? 0)
  const queueCapacity = Math.min(100, Number(summary.capacity ?? 100))
  const projectedQueueCount = queueCount - selectedAssignedIds.length + selectedReplacedIds.length
  const withinCapacity = projectedQueueCount >= 0 && projectedQueueCount <= queueCapacity

  const canSwap = useMemo(() => (
    viewer.can_swap_assignments &&
    !loading &&
    (selectedAssignedIds.length > 0 || selectedReplacedIds.length > 0) &&
    withinCapacity &&
    selectedReplacedIds.every((id) => selectedReplacedById[id]?.restore_eligible === true)
  ), [
    viewer.can_swap_assignments,
    loading,
    selectedAssignedIds.length,
    selectedReplacedIds,
    selectedReplacedById,
    withinCapacity
  ])

  const fillCurrent = Number(
    fillPreview.current ?? summary.assigned ?? 0
  )
  const fillCapacity = Number(
    fillPreview.capacity ?? summary.capacity ?? 0
  )
  const fillNeed = Math.max(
    Number(fillPreview.need ?? (fillCapacity - fillCurrent)),
    0
  )
  const fillEligible = Number(fillPreview.eligible ?? 0)
  const fillWillAssign = Number(
    fillPreview.will_assign ?? Math.min(fillNeed, fillEligible)
  )
  const canFillBucket = viewer.can_fill_bucket && fillNeed > 0

  const applyAssignedSearch = () => {
    setAssignedPage(1)
    setSelectedAssignedIds([])
    setSelectedAssignedById({})
    setAssignedQ(assignedQInput.trim())
  }

  const applyReplacedSearch = () => {
    setReplacedPage(1)
    setSelectedReplacedIds([])
    setSelectedReplacedById({})
    setReplacedQ(replacedQInput.trim())
  }

  const changeAssignedFilter = (setter) => (event) => {
    setter(event.target.value)
    setAssignedPage(1)
    setSelectedAssignedIds([])
    setSelectedAssignedById({})
  }

  const changeReplacedFilter = (setter) => (event) => {
    setter(event.target.value)
    setReplacedPage(1)
    setSelectedReplacedIds([])
    setSelectedReplacedById({})
  }

  const performSwap = async () => {
    if (!canSwap || swapping) return
    setSwapping(true)
    setErrorMessage('')
    try {
      const response = await SalesIgStoryApi.swapSaAssignments({
        administratorId,
        assignedAssignmentIds: selectedAssignedIds,
        replacedAssignmentIds: selectedReplacedIds
      })
      setMessage(response?.data?.message || `${selectedAssignedIds.length} moved to REPLACED; ${selectedReplacedIds.length} moved to ASSIGNED.`)
      setSelectedAssignedIds([])
      setSelectedReplacedIds([])
      setSelectedAssignedById({})
      setSelectedReplacedById({})
      setConfirmOpen(false)
      await load()
    }
    catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Gagal melakukan swap assignment.')
      setConfirmOpen(false)
    }
    finally {
      setSwapping(false)
    }
  }

  const performFillBucket = async () => {
    if (!canFillBucket || filling) return

    setFilling(true)
    setErrorMessage('')

    try {
      const response = await SalesIgStoryApi.fillSaBucket({
        administratorId
      })

      setMessage(
        response?.data?.message ||
        'Bucket assignment berhasil diperbarui.'
      )
      setFillConfirmOpen(false)
      await load()
    }
    catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
        'Gagal mengisi bucket assignment.'
      )
      setFillConfirmOpen(false)
    }
    finally {
      setFilling(false)
    }
  }

  const admin = member?.administrator || {}

  return (
    <Container fluid className='ig-story-page ig-story-sa-assignment-detail-page'>
      <TitlePage mainTitle='IG Story' subTitle='SA Assignment Detail' />

      <div className='d-flex flex-wrap justify-content-between align-items-center mb-3'>
        <Button color='secondary' outline onClick={() => history.push('/app/sales/ig-story/audit-queue')}>
          <i className='fas fa-arrow-left mr-2' />Back to Audit Queue
        </Button>
        {viewer.can_fill_bucket && (
          <div className='d-flex flex-wrap align-items-center mt-2 mt-md-0'>
            <div className='text-muted mr-3'>Bucket controls</div>
            <Button
              color='success'
              disabled={!canFillBucket || filling}
              onClick={() => setFillConfirmOpen(true)}
            >
              {filling
                ? <Spinner size='sm' className='mr-2' />
                : <i className='fas fa-plus-circle mr-2' />}
              Fill Bucket
            </Button>
          </div>
        )}
      </div>

      {message && <Alert color='success' toggle={() => setMessage('')}>{message}</Alert>}
      {errorMessage && <Alert color='danger' toggle={() => setErrorMessage('')}>{errorMessage}</Alert>}

      <Card className='mb-4'>
        <CardBody>
          <Row>
            <Col md='4' className='mb-3 mb-md-0'>
              <small className='text-muted'>Sales Assistant</small>
              <h3 className='mb-1'>{getIgStorySaDisplayName(admin) || `SA #${administratorId}`}</h3>
              <div className='text-muted'>{admin.email || '-'}</div>
              <Badge color={member?.active ? 'success' : 'secondary'} className='mt-2'>
                {member?.active ? 'Active' : 'Inactive'}
              </Badge>
            </Col>
            <Col md='2' xs='6'><small className='text-muted'>Category</small><h4>{member?.internal_category || '-'}</h4></Col>
            <Col md='2' xs='6'><small className='text-muted'>Assigned</small><h4>{summary.assigned || 0} / {summary.capacity || '-'}</h4></Col>
            <Col md='2' xs='6'><small className='text-muted'>Universal REPLACE</small><h4>{summary.replaced || 0}</h4></Col>
            <Col md='2' xs='6'><small className='text-muted'>Completed</small><h4>{summary.completed || 0}</h4></Col>
          </Row>
        </CardBody>
      </Card>

      <Card className='mb-4'>
        <CardBody>
          <div className='d-flex flex-wrap justify-content-between align-items-center mb-3'>
            <div>
              <h3 className='mb-1'>Assignment Composition</h3>
              <div className='text-muted'>Select ASSIGNED items to move out, REPLACED items to bring in, or both. The final queue cannot exceed this SA's capacity.</div>
            </div>
          </div>

          {viewer.can_swap_assignments && (
            <div className='ig-story-swap-toolbar mb-4'>
              <div>
                <strong>{selectedAssignedIds.length}</strong> ASSIGNED selected · <strong>{selectedReplacedIds.length}</strong> REPLACED selected
                {(selectedAssignedIds.length || selectedReplacedIds.length) > 0 && (
                  <div className={withinCapacity ? 'small text-muted' : 'small text-danger'}>
                    Final queue: {projectedQueueCount} / {queueCapacity}
                    {!withinCapacity && ' — capacity exceeded'}
                  </div>
                )}
              </div>
              <Button color='primary' disabled={!canSwap || swapping} onClick={() => setConfirmOpen(true)}>
                {swapping ? <Spinner size='sm' className='mr-2' /> : <i className='fas fa-exchange-alt mr-2' />}
                Flexible Swap
              </Button>
            </div>
          )}

          {loading && <div className='text-center py-3'><Spinner /> Loading assignments...</div>}

          <div className='d-flex flex-wrap justify-content-between align-items-center mb-2'>
            <h4 className='mb-0'>Assigned</h4>
            <InputGroup className='ig-story-assignment-search mt-2 mt-md-0'>
              <Input aria-label='Search ASSIGNED' value={assignedQInput} placeholder='Search SKU or product'
                onChange={(event) => setAssignedQInput(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') applyAssignedSearch() }} />
              <InputGroupAddon addonType='append'>
                <Button color='primary' outline onClick={applyAssignedSearch}>Search</Button>
              </InputGroupAddon>
            </InputGroup>
          </div>
          <Row form className='mb-3'>
            <Col md='4' className='mb-2'>
              <small className='text-muted d-block mb-1'>New Arrival dari (WIB)</small>
              <Input
                aria-label='Assigned New Arrival From'
                type='datetime-local'
                value={assignedArrivalFrom}
                onChange={changeAssignedFilter(setAssignedArrivalFrom)}
              />
            </Col>
            <Col md='4' className='mb-2'>
              <small className='text-muted d-block mb-1'>New Arrival sampai (WIB)</small>
              <Input
                aria-label='Assigned New Arrival To'
                type='datetime-local'
                value={assignedArrivalTo}
                onChange={changeAssignedFilter(setAssignedArrivalTo)}
              />
            </Col>
            <Col md='4' className='mb-2'>
              <small className='text-muted d-block mb-1'>Count</small>
              <Input
                aria-label='Assigned Count'
                type='select'
                value={assignedCountFilter}
                onChange={changeAssignedFilter(setAssignedCountFilter)}
              >
                <CountOptions />
              </Input>
            </Col>
          </Row>
          <div className='table-responsive'>
            <Table bordered hover size='sm'>
              <thead className='thead-light'><tr>
                <th className='text-center' style={{ width: 56 }}>No.</th>
                {viewer.can_swap_assignments && <th className='text-center'>Select</th>}
                <th>SKU</th><th>Product</th><th>Availability</th><th>New Arrival</th><th>Count</th><th>Status</th>
              </tr></thead>
              <tbody>
                {!loading && assigned.length === 0 && <tr><td colSpan={viewer.can_swap_assignments ? 8 : 7} className='text-center text-muted'>No ASSIGNED products.</td></tr>}
                {!loading && assigned.map((row, index) => <tr key={row.id}>
                  <td className='text-center'>{((Number(assignedMeta.current_page) || 1) - 1) * (Number(assignedMeta.per_page) || 15) + index + 1}</td>
                  {viewer.can_swap_assignments && <td className='text-center'><input type='checkbox'
                    aria-label={`Select assigned ${row.product?.sku || row.id}`}
                    checked={selectedAssignedIds.includes(row.id)}
                    onChange={(event) => setRowSelection({
                      checked: event.target.checked,
                      idSetter: setSelectedAssignedIds,
                      rowSetter: setSelectedAssignedById,
                      row
                    })}
                  /></td>}
                  <td><strong>{row.product?.sku || '-'}</strong></td>
                  <td>{row.product?.name || '-'}</td>
                  <td><AvailabilityBadge product={row.product} /></td>
                  <td>{formatDateTime(row.arrival_at)}</td>
                  <td>{row.upload_count || 0}</td>
                  <td><Badge color='primary'>ASSIGNED</Badge></td>
                </tr>)}
              </tbody>
            </Table>
          </div>
          <QueuePagination meta={assignedMeta} loading={loading} onChange={setAssignedPage} />

          <hr className='my-4' />

          <div className='d-flex flex-wrap justify-content-between align-items-center mb-2'>
            <div>
              <h4 className='mb-1'>Universal REPLACE Bucket</h4>
              <div className='text-muted mb-3'>Shared across all SA in the same category. Only Category <strong>{member?.internal_category || '-'}</strong> products can be assigned to this SA. Previous SA remains visible for audit history.</div>
            </div>
            <InputGroup className='ig-story-assignment-search mt-2 mt-md-0'>
              <Input aria-label='Search Universal REPLACE' value={replacedQInput} placeholder='Search SKU or product'
                onChange={(event) => setReplacedQInput(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') applyReplacedSearch() }} />
              <InputGroupAddon addonType='append'>
                <Button color='primary' outline onClick={applyReplacedSearch}>Search</Button>
              </InputGroupAddon>
            </InputGroup>
          </div>
          <Row form className='mb-3'>
            <Col md='4' className='mb-2'>
              <small className='text-muted d-block mb-1'>Replaced At dari (WIB)</small>
              <Input
                aria-label='Replaced At From'
                type='datetime-local'
                value={replacedAtFrom}
                onChange={changeReplacedFilter(setReplacedAtFrom)}
              />
            </Col>
            <Col md='4' className='mb-2'>
              <small className='text-muted d-block mb-1'>Replaced At sampai (WIB)</small>
              <Input
                aria-label='Replaced At To'
                type='datetime-local'
                value={replacedAtTo}
                onChange={changeReplacedFilter(setReplacedAtTo)}
              />
            </Col>
            <Col md='4' className='mb-2'>
              <small className='text-muted d-block mb-1'>Count</small>
              <Input
                aria-label='Replaced Count'
                type='select'
                value={replacedCountFilter}
                onChange={changeReplacedFilter(setReplacedCountFilter)}
              >
                <CountOptions />
              </Input>
            </Col>
          </Row>
          <div className='table-responsive'>
            <Table bordered hover size='sm'>
              <thead className='thead-light'><tr>
                <th className='text-center' style={{ width: 56 }}>No.</th>
                {viewer.can_swap_assignments && <th className='text-center'>Select</th>}
                <th>SKU</th><th>Product</th><th>Availability</th><th>Previous SA</th><th>Replaced At</th><th>Count</th><th>Status</th>
              </tr></thead>
              <tbody>
                {!loading && replaced.length === 0 && <tr><td colSpan={viewer.can_swap_assignments ? 9 : 8} className='text-center text-muted'>No products in Universal REPLACE Bucket.</td></tr>}
                {!loading && replaced.map((row, index) => <tr key={row.id} className={!row.restore_eligible ? 'ig-story-replaced-unavailable' : ''}>
                  <td className='text-center'>{((Number(replacedMeta.current_page) || 1) - 1) * (Number(replacedMeta.per_page) || 15) + index + 1}</td>
                  {viewer.can_swap_assignments && <td className='text-center'><input type='checkbox'
                    aria-label={`Select replaced ${row.product?.sku || row.id}`}
                    disabled={!row.restore_eligible}
                    checked={selectedReplacedIds.includes(row.id)}
                    onChange={(event) => setRowSelection({
                      checked: event.target.checked,
                      idSetter: setSelectedReplacedIds,
                      rowSetter: setSelectedReplacedById,
                      row
                    })}
                  /></td>}
                  <td><strong>{row.product?.sku || '-'}</strong></td>
                  <td>{row.product?.name || '-'}{!row.restore_eligible && <div className='small text-danger'>{row.restore_reason || 'Unavailable for restore'}</div>}</td>
                  <td><AvailabilityBadge product={row.product} /></td>
                  <td>{getIgStorySaDisplayName(row.assignee)}</td>
                  <td>{formatDateTime(row.replaced_at)}</td>
                  <td>{row.upload_count || 0}</td>
                  <td><Badge color='warning'>REPLACED</Badge></td>
                </tr>)}
              </tbody>
            </Table>
          </div>
          <QueuePagination meta={replacedMeta} loading={loading} onChange={setReplacedPage} />
        </CardBody>
      </Card>

      <Modal
        isOpen={fillConfirmOpen}
        toggle={() => !filling && setFillConfirmOpen(false)}
      >
        <ModalHeader
          toggle={() => !filling && setFillConfirmOpen(false)}
        >
          Confirm Fill Bucket
        </ModalHeader>
        <ModalBody>
          <p className='mb-3'>
            Fill this SA bucket with the newest eligible products until the configured capacity is reached.
          </p>

          <div className='d-flex justify-content-between mb-2'>
            <span className='text-muted'>Current</span>
            <strong>{fillCurrent}</strong>
          </div>
          <div className='d-flex justify-content-between mb-2'>
            <span className='text-muted'>Capacity</span>
            <strong>{fillCapacity}</strong>
          </div>
          <div className='d-flex justify-content-between mb-2'>
            <span className='text-muted'>Need</span>
            <strong>{fillNeed}</strong>
          </div>
          <div className='d-flex justify-content-between mb-2'>
            <span className='text-muted'>Eligible</span>
            <strong>{fillEligible}</strong>
          </div>
          <div className='d-flex justify-content-between'>
            <span className='text-muted'>Will assign</span>
            <strong>{fillWillAssign}</strong>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color='secondary'
            outline
            disabled={filling}
            onClick={() => setFillConfirmOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color='success'
            disabled={!canFillBucket || filling}
            onClick={performFillBucket}
          >
            {filling && <Spinner size='sm' className='mr-2' />}
            Confirm Fill
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={confirmOpen} toggle={() => !swapping && setConfirmOpen(false)}>
        <ModalHeader toggle={() => !swapping && setConfirmOpen(false)}>Confirm Flexible Swap</ModalHeader>
        <ModalBody>
          <p className='mb-3'>
            You are about to move <strong>{selectedAssignedIds.length}</strong> active assignment(s) to the Universal REPLACE Bucket and assign <strong>{selectedReplacedIds.length}</strong> product(s) from that shared bucket to this SA as new ASSIGNED cycles. Final queue: <strong>{projectedQueueCount}</strong> / <strong>{queueCapacity}</strong>.
          </p>

          <Row>
            <Col md='6' className='mb-3 mb-md-0'>
              <div className='font-weight-bold mb-2'>Move to Universal REPLACE</div>
              <div className='border rounded p-2'>
                {selectedAssignedIds.length === 0 && <span className='text-muted'>None</span>}
                {selectedAssignedIds.map((id) => {
                  const row = selectedAssignedById[id]
                  return (
                    <div key={id} className='py-1'>
                      <strong>{row?.product?.sku || `Assignment #${id}`}</strong>
                    </div>
                  )
                })}
              </div>
            </Col>

            <Col md='6'>
              <div className='font-weight-bold mb-2'>Assign to this SA</div>
              <div className='border rounded p-2'>
                {selectedReplacedIds.length === 0 && <span className='text-muted'>None</span>}
                {selectedReplacedIds.map((id) => {
                  const row = selectedReplacedById[id]
                  return (
                    <div key={id} className='py-1'>
                      <strong>{row?.product?.sku || `Assignment #${id}`}</strong>
                    </div>
                  )
                })}
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color='secondary' outline disabled={swapping} onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color='primary' disabled={!canSwap || swapping} onClick={performSwap}>
            {swapping && <Spinner size='sm' className='mr-2' />}Confirm Swap
          </Button>
        </ModalFooter>
      </Modal>
    </Container>
  )
}

SalesIgStorySaAssignmentDetailPage.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired }).isRequired,
  match: PropTypes.shape({ params: PropTypes.shape({ administratorId: PropTypes.string.isRequired }).isRequired }).isRequired
}

export default SalesIgStorySaAssignmentDetailPage
